import { AlertTriangle, Clock } from 'lucide-react';
import React, { useMemo } from 'react';
import { DEMO_TRANSACTIONS_LEGACY } from '@/data/demo-data';
import { BANK_ACCOUNTS } from '@/data/financial-systems';
import { useBaseForecast } from '@/hooks/useBaseForecast';
import { useFormatting } from '@/hooks/useFormatting';
import { getCashMovementTerms } from '@/lib/terminology-config';

export const DailyCashPositionPreview: React.FC = () => {
  const { formatCurrency, formatDate } = useFormatting();
  const terms = getCashMovementTerms();
  const { baseForecast: forecastData } = useBaseForecast();

  const today = forecastData[0];
  const tomorrow = forecastData[1];
  const openingBalance = BANK_ACCOUNTS.reduce((sum, a) => sum + a.balance, 0);

  // Next 7 days summary
  const next7Days = useMemo(
    () =>
      forecastData.slice(1, 8).map((d, i) => ({
        date: d.date,
        inflows: d.incoming,
        outflows: d.outgoing,
        closingBalance: d.balance,
        dayLabel: d.date.toLocaleDateString('en-GB', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
        }),
      })),
    [forecastData],
  );

  // Upcoming outstanding items from demo transactions
  const upcoming = DEMO_TRANSACTIONS_LEGACY.filter(
    (t) => t.status === 'Outstanding' && t.date > new Date(),
  )
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .slice(0, 8);

  const totalScheduledIn = upcoming
    .filter((t) => t.amount > 0)
    .reduce((s, t) => s + t.amount, 0);
  const totalScheduledOut = upcoming
    .filter((t) => t.amount < 0)
    .reduce((s, t) => s + Math.abs(t.amount), 0);

  // Minimum balance in next 7 days
  const minBalance = Math.min(...next7Days.map((d) => d.closingBalance));
  const cashWarning = minBalance < 200_000;

  return (
    <div className="space-y-6">
      {/* Today's position */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-muted/40 p-4">
          <p className="text-xs text-muted-foreground mb-1">
            Opening Balance (Today)
          </p>
          <p className="text-xl font-bold">{formatCurrency(openingBalance)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            As of{' '}
            {new Date().toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
            })}
          </p>
        </div>
        <div className="rounded-lg border bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800 p-4">
          <p className="text-xs text-muted-foreground mb-1">
            Scheduled {terms.in} (7 days)
          </p>
          <p className="text-xl font-bold text-green-700 dark:text-green-400">
            {formatCurrency(totalScheduledIn)}
          </p>
        </div>
        <div className="rounded-lg border bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800 p-4">
          <p className="text-xs text-muted-foreground mb-1">
            Scheduled {terms.out} (7 days)
          </p>
          <p className="text-xl font-bold text-red-700 dark:text-red-400">
            {formatCurrency(totalScheduledOut)}
          </p>
        </div>
        <div
          className={`rounded-lg border p-4 ${cashWarning ? 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800' : 'bg-muted/40'}`}
        >
          <p className="text-xs text-muted-foreground mb-1">
            Minimum Balance (7 days)
          </p>
          <p
            className={`text-xl font-bold ${cashWarning ? 'text-amber-700 dark:text-amber-400' : ''}`}
          >
            {formatCurrency(minBalance)}
          </p>
          {cashWarning && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Monitor closely
            </p>
          )}
        </div>
      </div>

      {/* 7-day daily view */}
      <div>
        <p className="text-sm font-semibold mb-3">
          Daily Balance — Next 7 Days
        </p>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/60">
                <th className="px-4 py-3 text-left font-semibold">Date</th>
                <th className="px-4 py-3 text-right font-semibold text-green-700 dark:text-green-400">
                  {terms.in}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-red-700 dark:text-red-400">
                  {terms.out}
                </th>
                <th className="px-4 py-3 text-right font-semibold">
                  Closing Balance
                </th>
              </tr>
            </thead>
            <tbody>
              {next7Days.map((day, i) => (
                <tr
                  key={i}
                  className={`border-b hover:bg-muted/30 transition-colors ${day.closingBalance < 150_000 ? 'bg-amber-50/50 dark:bg-amber-950/10' : ''}`}
                >
                  <td className="px-4 py-2.5">{day.dayLabel}</td>
                  <td className="px-4 py-2.5 text-right text-green-700 dark:text-green-400">
                    {day.inflows > 0 ? formatCurrency(day.inflows) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right text-red-700 dark:text-red-400">
                    {day.outflows > 0 ? formatCurrency(day.outflows) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium">
                    {formatCurrency(day.closingBalance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upcoming commitments */}
      <div>
        <p className="text-sm font-semibold mb-3">
          Upcoming Commitments &amp; Expected Receipts
        </p>
        <div className="space-y-2">
          {upcoming.map((t) => (
            <div
              key={t.id}
              className={`flex items-center justify-between rounded-lg border px-4 py-3 ${t.amount > 0 ? 'border-green-200 bg-green-50/50 dark:bg-green-950/20 dark:border-green-800' : 'border-red-200 bg-red-50/50 dark:bg-red-950/20 dark:border-red-800'}`}
            >
              <div className="flex items-center gap-3">
                <Clock
                  className={`h-4 w-4 flex-shrink-0 ${t.amount > 0 ? 'text-green-600' : 'text-red-600'}`}
                />
                <div>
                  <p className="text-sm font-medium">{t.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(t.date)} · {t.category}
                  </p>
                </div>
              </div>
              <span
                className={`font-semibold text-sm ${t.amount > 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}
              >
                {t.amount > 0 ? '+' : ''}
                {formatCurrency(t.amount)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
